import { useState } from 'react';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      const params = new URLSearchParams();
      params.append('email', email);
      const response = await fetch('https://readdy.ai/api/form/d3c52mtjjqdr4q69hqrg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-slate-950 text-slate-300">
      {/* Top bar */}
      <div className="border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
            <div>
              <div className="text-white font-bold text-lg mb-1">Stay Connected</div>
              <p className="text-slate-400 text-sm">Health tips and updates delivered to your inbox</p>
            </div>
            <form onSubmit={handleSubmit} data-readdy-form className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="px-4 py-2.5 bg-slate-800 border border-slate-700 text-white rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent w-full sm:w-72 placeholder-slate-500"
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-5 py-2.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer disabled:opacity-60"
              >
                {isSubmitting ? 'Subscribing...' : 'Subscribe'}
              </button>
            </form>
            {submitStatus === 'success' && (
              <span className="text-amber-400 text-sm flex items-center gap-2">
                <i className="ri-checkbox-circle-line"></i>Subscribed!
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Main footer */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-700 rounded-xl flex items-center justify-center">
                <i className="ri-heart-pulse-fill text-white text-lg"></i>
              </div>
              <div>
                <div className="text-white font-bold text-base leading-tight">Celebrity Home Health™</div>
                <div className="text-amber-500 text-xs">Medicare Certified · ACHC Accredited</div>
              </div>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed mb-4">
              Medicare certified and ACHC accredited home health care in Las Vegas. Compassionate, expert care delivered in the comfort of your home.
            </p>
            <p className="text-slate-500 text-xs leading-relaxed mb-6">
              Administrator: Dr. Osmel Villarreal, DBA, MBA, MS
            </p>
            <div className="flex gap-2">
              {[
                { icon: 'ri-facebook-fill', href: '#' },
                { icon: 'ri-instagram-line', href: '#' },
                { icon: 'ri-linkedin-fill', href: '#' },
              ].map((social, i) => (
                <a
                  key={i}
                  href={social.href}
                  className="w-9 h-9 bg-slate-800 hover:bg-amber-600 text-slate-400 hover:text-white rounded-lg flex items-center justify-center transition-all duration-300 cursor-pointer"
                >
                  <i className={`${social.icon} text-sm`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-5">Navigation</h3>
            <ul className="space-y-3">
              {[
                { href: '/', label: 'Home', external: false },
                { href: '/about', label: 'About Us', external: false },
                { href: '/services', label: 'Services', external: false },
                { href: '/contact', label: 'Contact', external: false },
              ].map((link) => (
                <li key={link.href}>
                  <a href={link.href} className="text-slate-400 hover:text-amber-400 text-sm transition-colors duration-200 flex items-center gap-2 group cursor-pointer">
                    <i className="ri-arrow-right-s-line text-amber-600 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></i>
                    {link.label}
                  </a>
                </li>
              ))}
              <li>
                <a href="https://ats.rippling.com/celebrityhh/jobs" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-amber-400 text-sm transition-colors duration-200 flex items-center gap-2 group cursor-pointer">
                  <i className="ri-arrow-right-s-line text-amber-600 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></i>
                  Careers &amp; Jobs <i className="ri-external-link-line text-xs opacity-50"></i>
                </a>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-5">Our Services</h3>
            <ul className="space-y-3">
              {[
                'Skilled Nursing',
                'Physical Therapy',
                'Occupational Therapy',
                'Speech Therapy',
                'Medical Social Services',
                'Home Health Aide',
              ].map((service) => (
                <li key={service}>
                  <a href="/services" className="text-slate-400 hover:text-amber-400 text-sm transition-colors duration-200 flex items-center gap-2 group cursor-pointer">
                    <i className="ri-arrow-right-s-line text-amber-600 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></i>
                    {service}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-5">Get In Touch</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 bg-slate-800 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-map-pin-line text-amber-500 text-sm"></i>
                </div>
                <span className="text-slate-400 text-sm">2121 E Flamingo Rd Ste 218A<br />Las Vegas, NV 89119</span>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-8 h-8 bg-slate-800 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-line text-amber-500 text-sm"></i>
                </div>
                <a href="tel:866-939-0330" className="text-slate-400 hover:text-amber-400 text-sm transition-colors duration-200 cursor-pointer">866-939-0330</a>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-8 h-8 bg-slate-800 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-amber-500 text-sm"></i>
                </div>
                <a href="mailto:info@celebrity-homehealth.com" className="text-slate-400 hover:text-amber-400 text-sm transition-colors duration-200 cursor-pointer">info@celebrity-homehealth.com</a>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-8 h-8 bg-slate-800 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-line text-amber-500 text-sm"></i>
                </div>
                <span className="text-slate-400 text-sm">Mon–Fri 8AM–5PM<br />Sat 9AM–2PM</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-6 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm">&copy; 2026 Celebrity Home Health™ — celebrity-homehealth.com. All rights reserved.</p>
          <div className="flex flex-wrap gap-6">
            <a href="#" className="text-slate-500 hover:text-amber-400 text-sm transition-colors duration-200 cursor-pointer">Privacy Policy</a>
            <a href="#" className="text-slate-500 hover:text-amber-400 text-sm transition-colors duration-200 cursor-pointer">HIPAA Notice</a>
            <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-amber-400 text-sm transition-colors duration-200">Powered by Readdy</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
